import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Zap, Boxes, Wand2, Share2, ShieldCheck, ArrowRight, Film, Play, Pause, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";

const AFRICAN_SAMPLES = [
  { key: "olufunmilola", name: "Olufunmilola", desc: "Nigerian · warm female", src: "/voices/olufunmilola.mp3" },
  { key: "terna", name: "Terna Gabriel", desc: "Nigerian · deep male", src: "/voices/terna.mp3" },
  { key: "taiwo", name: "Taiwo", desc: "African · natural female", src: "/voices/taiwo.mp3" },
  { key: "ade", name: "Ade", desc: "African · calm male", src: "/voices/ade.mp3" },
];

function VoiceSampler() {
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const toggle = (v) => {
    if (playing === v.key) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    const a = new Audio(v.src);
    audioRef.current = a;
    a.onended = () => setPlaying(null);
    a.play().catch(() => setPlaying(null));
    setPlaying(v.key);
  };
  return (
    <section className="relative px-6 max-w-7xl mx-auto pb-32" data-testid="voices-section">
      <div className="inline-flex items-center gap-2 text-[#39FF14] text-xs font-medium mb-3">
        <Mic className="w-4 h-4" /> NEW · African voice-overs
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-3">Narrate in an authentic African voice</h2>
      <p className="text-white/50 font-light mb-12 max-w-2xl">Tap to hear real African-accent narrators — or clone your own voice — and narrate any film you create.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {AFRICAN_SAMPLES.map((v) => (
          <button key={v.key} onClick={() => toggle(v)} data-testid={`voice-sample-${v.key}`}
            className={`flex items-center gap-3 text-left bg-zinc-950 border rounded-lg p-4 transition-colors ${playing === v.key ? "border-[#39FF14]" : "border-white/10 hover:border-[#00E5FF]/40"}`}>
            <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${playing === v.key ? "bg-[#39FF14] text-black" : "bg-[#00E5FF]/15 text-[#00E5FF]"}`}>
              {playing === v.key ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </span>
            <span>
              <span className="block font-medium text-sm">{v.name}</span>
              <span className="block text-[11px] text-white/45">{v.desc}</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

const features = [
  { icon: Boxes, title: "3D / HD Transform", desc: "Turn flat clips into cinematic depth with a 4K HDR-style treatment preview." },
  { icon: Wand2, title: "AI Story Enhancer", desc: "Get a stronger hook, scene beats and 6 creative ideas that make the story sweeter." },
  { icon: Sparkles, title: "Original Graphics", desc: "Generate copyright-safe backgrounds, thumbnails and art that fit your story." },
  { icon: Share2, title: "Viral Copy Engine", desc: "Platform-native captions + hashtags for TikTok, Reels, Shorts, X, FB & LinkedIn." },
  { icon: ShieldCheck, title: "Copyright-Safe", desc: "Everything generated is 100% original and publishable in your own rights." },
  { icon: Zap, title: "Built for Virality", desc: "Every output is tuned to stop the scroll and go viral across all platforms." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#050505] text-white">
      {/* nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-8 h-8 rounded-md bg-[#00E5FF] flex items-center justify-center glow-cyan">
              <Film className="w-5 h-5 text-black" />
            </div>
            <span className="font-display text-lg font-semibold tracking-tight">VIRAL3D</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/5"
              onClick={() => navigate("/login")} data-testid="nav-login-btn">Sign in</Button>
            <Button className="rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium"
              onClick={() => navigate("/register")} data-testid="nav-signup-btn">
              Start free <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative pt-40 pb-28 px-6 max-w-7xl mx-auto">
        <div className="absolute -top-20 -left-40 w-[500px] h-[500px] rounded-full bg-[#00E5FF]/10 blur-[120px] pointer-events-none" />
        <div className="absolute top-40 right-0 w-[400px] h-[400px] rounded-full bg-[#FF007F]/10 blur-[120px] pointer-events-none" />
        <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 text-xs text-white/70 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" /> AI Video Studio
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-none">
              Transform any video into a <span className="text-[#00E5FF] text-glow-cyan">viral 3D</span> masterpiece
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-base md:text-lg text-white/60 font-light max-w-xl leading-relaxed">
              Upload a clip and let AI rewrite the story, craft original graphics, apply a cinematic 3D / HD
              treatment and generate viral-ready captions for every platform — all copyright-safe, in your own rights.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-9 flex flex-wrap gap-4">
              <Button className="rounded-full h-12 px-7 bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium text-base glow-cyan"
                onClick={() => navigate("/register")} data-testid="hero-cta-btn">
                Create your first video <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button variant="outline" className="rounded-full h-12 px-7 border-white/20 bg-transparent text-white hover:bg-white/5"
                onClick={() => navigate("/login")} data-testid="hero-signin-btn">
                I already have an account
              </Button>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="relative">
            <div className="rounded-xl overflow-hidden border border-white/10 preview-3d active">
              <img src="https://images.pexels.com/photos/11285726/pexels-photo-11285726.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="3D neon tunnel" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-black/80 backdrop-blur-xl border border-white/10 rounded-lg px-4 py-3 glow-pink">
              <p className="text-xs text-white/50">Rendering</p>
              <p className="font-display text-sm text-[#FF007F]">4K HDR · 3D Depth</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* features */}
      <section className="relative px-6 max-w-7xl mx-auto pb-32">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-3">Everything you need to go viral</h2>
        <p className="text-white/50 font-light mb-12 max-w-2xl">One studio to enhance, restyle and package your videos for every feed.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
              className="group bg-zinc-950 border border-white/10 rounded-lg p-6 hover:-translate-y-1 hover:border-[#00E5FF]/40 transition-[transform,border-color] duration-300"
              data-testid={`feature-card-${i}`}>
              <div className="w-11 h-11 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-4 group-hover:bg-[#00E5FF]/10">
                <f.icon className="w-5 h-5 text-[#00E5FF]" />
              </div>
              <h3 className="font-display text-lg font-semibold tracking-tight mb-2">{f.title}</h3>
              <p className="text-sm text-white/55 font-light leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <VoiceSampler />

      <footer className="border-t border-white/10 py-8 px-6 text-center text-white/40 text-sm">
        VIRAL3D Studio — Original, copyright-safe video transformation.
      </footer>
    </div>
  );
}
